import { Fragment, useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  RiCloudLine,
  RiDownloadLine,
  RiFileCopyLine,
} from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const AwsInfrastructureInfoCard = () => {
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations || {};

  const instanceType = annotations['aws.infrastructure/instance-type'];
  const instanceId = annotations['aws.infrastructure/instance-id'];
  const instanceName = annotations['aws.infrastructure/instance-name'];
  const publicIp = annotations['aws.infrastructure/public-ip'];
  const consoleUrl = annotations['aws.infrastructure/console-url'];
  const vpcId = annotations['aws.infrastructure/vpc-id'];
  const subnetId = annotations['aws.infrastructure/subnet-id'];
  const securityGroupId = annotations['aws.infrastructure/security-group-id'];
  const region = annotations['aws.infrastructure/region'];
  const environmentName =
    annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const sshCommandAnnotation = annotations['aws.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded =
    annotations['aws.infrastructure/ssh-private-key'];

  const splitList = (key: string): string[] => {
    const raw = annotations[key];
    return raw ? raw.split('|||').filter(Boolean) : [];
  };
  const instanceNames = splitList('aws.infrastructure/instance-names');
  const instanceIds = splitList('aws.infrastructure/instance-ids');
  const publicIps = splitList('aws.infrastructure/public-ips');
  const privateIps = splitList('aws.infrastructure/private-ips');
  const sshCommands = splitList('aws.infrastructure/ssh-commands');
  const isMultiVm = instanceNames.length > 1;
  const isEmptyVm = templateType === 'aws-empty-vm';

  const hasAwsInfo =
    instanceId || publicIp || vpcId || instanceType || isMultiVm;
  const isAWS = templateType?.startsWith('aws-');

  const cdcTypes = [
    'aws-postgres-cdc-source',
    'aws-rds-postgres-cdc-source',
    'aws-mysql-cdc-source',
    'aws-rds-mysql-cdc-source',
    'aws-mongodb-cdc-source',
    'aws-oracle-cdc-source',
    'aws-sqlserver-cdc-source',
    'aws-db2-cdc-source',
    'aws-cassandra-cdc-source',
  ];

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return atob(sshPrivateKeyEncoded);
    } catch {
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  if (
    !hasAwsInfo ||
    !isAWS ||
    templateType === 'aws-eks-cluster' ||
    templateType === 'aws-redis-crdb' ||
    templateType === 'aws-redis-oss-vm' ||
    templateType === 'aws-redis-oss-sentinel' ||
    templateType === 'aws-redis-enterprise' ||
    templateType === 'aws-rdi-server' ||
    templateType === 'aws-samba-ad-server' ||
    templateType === 'aws-vault-server' ||
    cdcTypes.includes(templateType || '')
  ) {
    return null;
  }

  const getAwsConsoleUrl = (resourceType: string, resourceId: string) => {
    const awsRegion = region || 'us-east-1';
    switch (resourceType) {
      case 'instance':
        return `https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion}#InstanceDetails:instanceId=${resourceId}`;
      case 'vpc':
        return `https://console.aws.amazon.com/vpc/home?region=${awsRegion}#VpcDetails:VpcId=${resourceId}`;
      case 'subnet':
        return `https://console.aws.amazon.com/vpc/home?region=${awsRegion}#SubnetDetails:subnetId=${resourceId}`;
      case 'sg':
        return `https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion}#SecurityGroup:groupId=${resourceId}`;
      default:
        return '';
    }
  };

  const title =
    templateType === 'aws-empty-vm'
      ? 'Empty AWS VM'
      : templateType === 'aws-memtier-benchmark'
        ? 'Memtier Benchmark VM'
        : templateType === 'aws-riotx'
          ? 'RIOT-X Migration VM'
          : 'AWS Infrastructure';

  const withKeyFlag = (cmd: string | undefined, ip: string | undefined) => {
    if (cmd?.includes('-i ')) return cmd;
    if (!ip) return cmd || '';
    return `ssh -i <PATH_TO_YOUR_PRIVATE_KEY> ubuntu@${ip}`;
  };
  const sshCommand = withKeyFlag(sshCommandAnnotation, publicIp);
  const downloadedKeyCommand = publicIp
    ? `ssh -i empty_vm_key.pem ubuntu@${publicIp}`
    : '';

  const downloadSSHKey = () => {
    if (!sshPrivateKey) return;
    const blob = new Blob([sshPrivateKey], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'empty_vm_key.pem';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <InfoCard
      title={title}
      subheader={`Environment: ${environmentName || 'N/A'}`}
      icon={<RiCloudLine size={20} />}
    >
      <InfraStack>
        <InfraSection
          title={`🖥️ ${isMultiVm ? `EC2 Instances (${instanceNames.length})` : 'EC2 Instance'}`}
        >
          {instanceType && (
            <InfraField label="Instance Type:" value={instanceType} />
          )}
          {region && <InfraField label="Region:" value={region} />}

          {!isMultiVm && instanceId && (
            <InfraField
              label="Instance ID:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink
                    href={
                      consoleUrl || getAwsConsoleUrl('instance', instanceId)
                    }
                  >
                    {instanceId}
                  </ExternalLink>
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
          )}
          {!isMultiVm && instanceName && (
            <InfraField label="Instance Name:" value={instanceName} />
          )}
          {!isMultiVm && publicIp && (
            <CopyableValue label="Public IP:" value={publicIp} />
          )}

          {isMultiVm &&
            instanceNames.map((vmName, idx) => {
              const vmId = instanceIds[idx];
              const vmPubIp = publicIps[idx];
              const vmPrivIp = privateIps[idx];
              const vmSsh = withKeyFlag(sshCommands[idx], vmPubIp);
              return (
                <Fragment key={vmName}>
                  <InfraField
                    label={`VM #${idx + 1}:`}
                    value={vmName}
                    mono={false}
                  />
                  {vmId && (
                    <InfraField
                      label="Instance ID:"
                      value={
                        <ExternalLink href={getAwsConsoleUrl('instance', vmId)}>
                          {vmId}
                        </ExternalLink>
                      }
                      mono={false}
                    />
                  )}
                  {vmPubIp && (
                    <CopyableValue label="Public IP:" value={vmPubIp} />
                  )}
                  {vmPrivIp && (
                    <InfraField label="Private IP:" value={vmPrivIp} />
                  )}
                  {vmSsh && <CopyableValue label="SSH Command:" value={vmSsh} />}
                </Fragment>
              );
            })}
        </InfraSection>

        {(sshCommand || sshPrivateKey || isEmptyVm) && (
          <InfraSection title="🔐 SSH Access">
            {sshPrivateKey ? (
              <>
                <Text variant="body-small">
                  A key pair was auto-generated for this VM. Download it, then
                  connect with <code>-i</code>:
                </Text>
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
                <Text variant="body-small">
                  Then run <code>chmod 400 empty_vm_key.pem</code>
                </Text>
                {downloadedKeyCommand && (
                  <CopyableValue
                    label="SSH Command:"
                    value={downloadedKeyCommand}
                  />
                )}
              </>
            ) : (
              <>
                {sshCommand && (
                  <CopyableValue label="SSH Command:" value={sshCommand} />
                )}
                <Text variant="body-small">
                  Replace <code>&lt;PATH_TO_YOUR_PRIVATE_KEY&gt;</code> with the
                  private key that matches the public key you pasted at
                  provision (e.g.{' '}
                  <code>ssh -i ~/.ssh/ps-empty-vm ubuntu@…</code>). A bare{' '}
                  <code>ssh user@ip</code> only works if that key is your SSH
                  default identity.
                </Text>
              </>
            )}
          </InfraSection>
        )}

        {(vpcId || subnetId || securityGroupId) && (
          <InfraSection title="🌐 Network">
            {vpcId && (
              <InfraField
                label="VPC ID:"
                value={
                  <ExternalLink href={getAwsConsoleUrl('vpc', vpcId)}>
                    {vpcId}
                  </ExternalLink>
                }
                mono={false}
              />
            )}
            {subnetId && (
              <InfraField
                label="Subnet ID:"
                value={
                  <ExternalLink href={getAwsConsoleUrl('subnet', subnetId)}>
                    {subnetId}
                  </ExternalLink>
                }
                mono={false}
              />
            )}
            {securityGroupId && (
              <InfraField
                label="Security Group:"
                value={
                  <ExternalLink href={getAwsConsoleUrl('sg', securityGroupId)}>
                    {securityGroupId}
                  </ExternalLink>
                }
                mono={false}
              />
            )}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
